import { telegramHref } from "@/lib/social-links";
import { withMessage } from "@/lib/contact-message";
import {
  WHATSAPP_ERROR,
  formatWhatsapp,
  looksLikePhone,
  normalizeWhatsapp,
  withWhatsapp,
} from "@/lib/whatsapp";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const MY_CONTACT_KEY = ["my-contact"] as const;

export type MyContact = { telegram: string | null; social_links: string[] };

// Контакт текущего пользователя — один запрос на весь экран.
export function useMyContact(userId: string | undefined) {
  return useQuery({
    queryKey: [...MY_CONTACT_KEY, userId],
    enabled: !!userId,
    queryFn: async (): Promise<MyContact> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("telegram, social_links")
        .eq("user_id", userId!)
        .maybeSingle();
      if (error) throw error;
      return {
        telegram: (data?.telegram as string | null) ?? null,
        social_links: ((data?.social_links as string[] | null) ?? []).filter(Boolean),
      };
    },
  });
}

export function hasAnyContact(c: MyContact | undefined): boolean {
  return !!c && (!!c.telegram?.trim() || c.social_links.length > 0);
}

// Похоже ли значение на телеграм: @username, t.me/... или просто слово латиницей.
export function isTelegramLike(v: string): boolean {
  return /^@?[a-zA-Z0-9_]{3,}$/.test(v) || /^(https?:\/\/)?t\.me\//.test(v);
}

export function normalizeTelegram(v: string): string {
  return v
    .replace(/^@/, "")
    .replace(/^(https?:\/\/)?t\.me\//, "")
    .replace(/\/$/, "");
}

// Подпись кнопки по домену ссылки.
export function contactLabel(url: string): string {
  const u = url.toLowerCase();
  if (u.includes("t.me")) return "Telegram";
  if (u.includes("instagram.com")) return "Instagram";
  if (u.includes("wa.me") || u.includes("api.whatsapp.com")) return "WhatsApp";
  return "Написать";
}

// Кнопки контактов: телеграм первым, затем соцссылки.
export function ContactButtons({
  telegram,
  links,
  onContact,
  message,
  buttonClassName,
}: {
  /** Необязательные классы кнопок (размер) — по умолчанию как раньше. */
  buttonClassName?: string;
  telegram?: string | null;
  links: string[];
  onContact?: (url: string) => void;
  /** Черновик для Telegram/WhatsApp. */
  message?: string;
}) {
  const tg = telegramHref(telegram);
  const tgHref = tg ? withMessage(tg, message) : null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tgHref && (
        <Button asChild size="sm" className={`bg-td-teal hover:bg-td-teal/90 ${buttonClassName ?? ""}`}>
          <a
            href={tgHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onContact?.(tgHref)}
          >
            Telegram
          </a>
        </Button>
      )}
      {links.map((url) => (
        <Button key={url} asChild size="sm" variant="outline" className={buttonClassName}>
          <a href={withMessage(url, message)} target="_blank" rel="noopener noreferrer" onClick={() => onContact?.(url)}>
            {contactLabel(url)}
          </a>
        </Button>
      ))}
    </div>
  );
}

// Поле «Твой контакт для встречи» — сохраняет в telegram или social_links.
export function ContactSaver({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const v = value.trim();
      if (!v) throw new Error("Впиши контакт");
      const phone = looksLikePhone(v);
      if (!phone && isTelegramLike(v)) {
        const { error } = await supabase
          .from("profiles")
          .update({ telegram: normalizeTelegram(v) })
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const digits = phone ? normalizeWhatsapp(v) : null;
        if (phone && !digits) throw new Error(WHATSAPP_ERROR);
        const { data: row, error: readError } = await supabase
          .from("profiles")
          .select("social_links")
          .eq("user_id", userId)
          .maybeSingle();
        if (readError) throw readError;
        const current = ((row?.social_links as string[] | null) ?? []).filter(Boolean);
        const next = digits ? withWhatsapp(current, digits) : [...current, v];
        const { error } = await supabase
          .from("profiles")
          .update({ social_links: next })
          .eq("user_id", userId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      setSaved(true);
      setValue("");
      toast.success("Контакт добавлен");
      qc.invalidateQueries({ queryKey: MY_CONTACT_KEY });
      qc.invalidateQueries({ queryKey: ["coffee-invites"] });
    },
    onError: (e: Error) => toast.error(e.message || "Не удалось сохранить"),
  });

  if (saved) return <p className="text-xs font-medium text-td-teal">Контакт добавлен</p>;
  const preview = looksLikePhone(value) ? normalizeWhatsapp(value) : null;

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-td-dark">Твой контакт для встречи</label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="@username в Telegram или номер WhatsApp"
          className="h-9 text-sm"
        />
        <Button
          size="sm"
          className="h-9 shrink-0 bg-td-teal hover:bg-td-teal/90"
          disabled={save.isPending || !value.trim()}
          onClick={() => save.mutate()}
        >
          Сохранить
        </Button>
      </div>
      {preview && <p className="text-xs text-td-teal">Сохраним как {formatWhatsapp(preview)}</p>}
    </div>
  );
}
